import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TtCourse, TtStudentRecord, CourseStatus } from '../types';
import type { PendingRecordDto } from '../services/trungTamApi';

/**
 * Per-section action — each residual list (pending, unreviewed) has its
 * own independent choice. `keep` = leave in the source course; `all` =
 * move every row; `selected` = only ticked IDs.
 */
type SectionAction = 'keep' | 'all' | 'selected';

export type CloseCourseSubmit = {
  targetStatus: CourseStatus;
  pending: {
    action: SectionAction;
    targetCourseId: number | null;
    ids: number[];
  };
  unreviewed: {
    action: SectionAction;
    targetCourseId: number | null;
    ids: number[];
  };
};

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (submit: CloseCourseSubmit) => void | Promise<void>;
  pendingRecords: PendingRecordDto[];
  unreviewedRecords: TtStudentRecord[];
  openCourses: TtCourse[];
  currentCourseId: number;
  currentCourseHang: string;
  targetStatus?: CourseStatus;
};

/**
 * Close-a-course confirmation modal with TWO independent transfer
 * sections — one for "chờ lưu hồ sơ" (tt_pending_record) and one for
 * "chưa duyệt" (tt_student_record, reviewed=false). Each section has
 * its own target course + bulk/pick mode.
 *
 * Target course list is filtered per-section to courses that are open
 * AND have the same hang as the source course.
 */
const CourseCloseModal: React.FC<Props> = ({
  open, onClose, onConfirm, pendingRecords, unreviewedRecords, openCourses,
  currentCourseId, currentCourseHang, targetStatus = 'closed',
}) => {
  const [pendingAction, setPendingAction] = useState<SectionAction>('keep');
  const [pendingTarget, setPendingTarget] = useState<number | null>(null);
  const [pendingPicked, setPendingPicked] = useState<Set<number>>(new Set());

  const [unreviewedAction, setUnreviewedAction] = useState<SectionAction>('keep');
  const [unreviewedTarget, setUnreviewedTarget] = useState<number | null>(null);
  const [unreviewedPicked, setUnreviewedPicked] = useState<Set<number>>(new Set());

  const [submitting, setSubmitting] = useState(false);

  const eligibleTargets = useMemo(
    () => openCourses.filter(c => c.id !== currentCourseId && c.hang === currentCourseHang),
    [openCourses, currentCourseId, currentCourseHang],
  );

  if (!open) return null;

  const isClosed = targetStatus === 'closed';
  const headerLabel = isClosed ? 'Đóng khóa học' : 'Chuyển sang chờ nhập CSĐT';
  const confirmLabel = isClosed ? 'Xác nhận & Đóng' : 'Xác nhận & Chuyển';
  const hasPending = pendingRecords.length > 0;
  const hasUnreviewed = unreviewedRecords.length > 0;
  const hasResiduals = hasPending || hasUnreviewed;

  const pendingIsValid =
    pendingAction === 'keep' ||
    (pendingAction === 'all' && pendingTarget !== null) ||
    (pendingAction === 'selected' && pendingTarget !== null && pendingPicked.size > 0);

  const unreviewedIsValid =
    unreviewedAction === 'keep' ||
    (unreviewedAction === 'all' && unreviewedTarget !== null) ||
    (unreviewedAction === 'selected' && unreviewedTarget !== null && unreviewedPicked.size > 0);

  const canConfirm = pendingIsValid && unreviewedIsValid;

  const togglePending = (id: number) => {
    setPendingPicked(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleUnreviewed = (id: number) => {
    setUnreviewedPicked(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm({
        targetStatus,
        pending: {
          action: pendingAction,
          targetCourseId: pendingAction === 'keep' ? null : pendingTarget,
          ids: pendingAction === 'all'
            ? pendingRecords.map(p => p.id)
            : pendingAction === 'selected'
              ? Array.from(pendingPicked)
              : [],
        },
        unreviewed: {
          action: unreviewedAction,
          targetCourseId: unreviewedAction === 'keep' ? null : unreviewedTarget,
          ids: unreviewedAction === 'all'
            ? unreviewedRecords.map(r => r.id)
            : unreviewedAction === 'selected'
              ? Array.from(unreviewedPicked)
              : [],
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderTargetSelector = (
    value: number | null,
    onChange: (v: number | null) => void,
    disabled: boolean,
  ) => {
    if (eligibleTargets.length === 0) {
      return (
        <p className="tt-modal__no-target">
          <i className="material-icons">warning</i>
          Không có khóa nào đang mở cùng hạng {currentCourseHang}.
        </p>
      );
    }
    return (
      <select
        value={value ?? ''}
        onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
        disabled={disabled}
      >
        <option value="">— Chọn khóa đích —</option>
        {eligibleTargets.map(c => (
          <option key={c.id} value={c.id}>
            {c.name} (Hạng {c.hang}, {c.recordCount ?? 0} hồ sơ)
          </option>
        ))}
      </select>
    );
  };

  return createPortal(
    <div className="tt-modal-overlay" onClick={onClose}>
      <div className="tt-modal tt-modal--close-course" onClick={e => e.stopPropagation()}>
        <div className="tt-modal__header">
          <h3>
            <i className="material-icons">warning</i>
            {headerLabel}
          </h3>
          <button type="button" className="tt-modal__close" onClick={onClose}>
            <i className="material-icons">close</i>
          </button>
        </div>

        <div className="tt-modal__body">
          {!hasResiduals && (
            <p className="tt-modal__alert">
              <i className="material-icons">check_circle</i>
              Khóa này không còn hồ sơ chờ lưu hay chưa duyệt. Có thể đóng an toàn.
            </p>
          )}

          {hasResiduals && (
            <p className="tt-modal__alert">
              <i className="material-icons">info</i>
              Khóa còn tồn:{' '}
              {hasPending && <><strong>{pendingRecords.length}</strong> chờ lưu</>}
              {hasPending && hasUnreviewed && ' · '}
              {hasUnreviewed && <><strong>{unreviewedRecords.length}</strong> chưa duyệt</>}
              . Chọn xử lý cho từng loại.
            </p>
          )}

          {/* ─── Section 1: chờ lưu hồ sơ ─── */}
          {hasPending && (
            <div className="tt-modal__section">
              <div className="tt-modal__section-title">
                <i className="material-icons">pending_actions</i>
                Chờ lưu hồ sơ ({pendingRecords.length})
              </div>
              <div className="tt-modal__options">
                <label className={`tt-modal__option ${pendingAction === 'keep' ? 'tt-modal__option--active' : ''}`}>
                  <input type="radio" checked={pendingAction === 'keep'} onChange={() => setPendingAction('keep')} />
                  <div><strong>Giữ tại khóa này</strong><span>Không chuyển — hồ sơ sẽ kẹt lại ở khóa đã đóng</span></div>
                </label>
                <label className={`tt-modal__option ${pendingAction === 'all' ? 'tt-modal__option--active' : ''}`}>
                  <input type="radio" checked={pendingAction === 'all'} onChange={() => setPendingAction('all')} />
                  <div><strong>Chuyển toàn bộ</strong><span>Chuyển cả {pendingRecords.length} hồ sơ chờ lưu</span></div>
                </label>
                <label className={`tt-modal__option ${pendingAction === 'selected' ? 'tt-modal__option--active' : ''}`}>
                  <input type="radio" checked={pendingAction === 'selected'} onChange={() => setPendingAction('selected')} />
                  <div><strong>Chọn từng hồ sơ</strong><span>Tick những hồ sơ muốn chuyển</span></div>
                </label>
              </div>

              {pendingAction !== 'keep' && (
                <div className="tt-modal__target">
                  <label>Khóa đích (đang mở · hạng {currentCourseHang}):</label>
                  {renderTargetSelector(pendingTarget, setPendingTarget, submitting)}
                </div>
              )}

              {pendingAction === 'selected' && (
                <div className="tt-modal__student-list">
                  <div className="tt-modal__student-list-header">
                    <span>Chọn hồ sơ ({pendingPicked.size}/{pendingRecords.length})</span>
                    <button
                      type="button"
                      className="tt-btn tt-btn--outline tt-btn--xs"
                      onClick={() => setPendingPicked(new Set(pendingRecords.map(p => p.id)))}
                    >
                      Chọn tất cả
                    </button>
                  </div>
                  <div className="tt-modal__student-items">
                    {pendingRecords.map(p => (
                      <label key={p.id} className="tt-modal__student-item">
                        <input type="checkbox" checked={pendingPicked.has(p.id)} onChange={() => togglePending(p.id)} />
                        <span>{p.hoTen || '(chưa có tên)'}</span>
                        <span className="tt-modal__student-cccd">{p.soCCCD || '—'}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── Section 2: chưa duyệt ─── */}
          {hasUnreviewed && (
            <div className="tt-modal__section">
              <div className="tt-modal__section-title">
                <i className="material-icons">fact_check</i>
                Chưa duyệt ({unreviewedRecords.length})
              </div>
              <div className="tt-modal__options">
                <label className={`tt-modal__option ${unreviewedAction === 'keep' ? 'tt-modal__option--active' : ''}`}>
                  <input type="radio" checked={unreviewedAction === 'keep'} onChange={() => setUnreviewedAction('keep')} />
                  <div><strong>Giữ tại khóa này</strong><span>Hồ sơ sẽ nằm trong khóa đã đóng, không thể duyệt thêm</span></div>
                </label>
                <label className={`tt-modal__option ${unreviewedAction === 'all' ? 'tt-modal__option--active' : ''}`}>
                  <input type="radio" checked={unreviewedAction === 'all'} onChange={() => setUnreviewedAction('all')} />
                  <div><strong>Chuyển toàn bộ</strong><span>Chuyển cả {unreviewedRecords.length} hồ sơ chưa duyệt</span></div>
                </label>
                <label className={`tt-modal__option ${unreviewedAction === 'selected' ? 'tt-modal__option--active' : ''}`}>
                  <input type="radio" checked={unreviewedAction === 'selected'} onChange={() => setUnreviewedAction('selected')} />
                  <div><strong>Chọn từng hồ sơ</strong><span>Tick những hồ sơ muốn chuyển</span></div>
                </label>
              </div>

              {unreviewedAction !== 'keep' && (
                <div className="tt-modal__target">
                  <label>Khóa đích (đang mở · hạng {currentCourseHang}):</label>
                  {renderTargetSelector(unreviewedTarget, setUnreviewedTarget, submitting)}
                </div>
              )}

              {unreviewedAction === 'selected' && (
                <div className="tt-modal__student-list">
                  <div className="tt-modal__student-list-header">
                    <span>Chọn hồ sơ ({unreviewedPicked.size}/{unreviewedRecords.length})</span>
                    <button
                      type="button"
                      className="tt-btn tt-btn--outline tt-btn--xs"
                      onClick={() => setUnreviewedPicked(new Set(unreviewedRecords.map(r => r.id)))}
                    >
                      Chọn tất cả
                    </button>
                  </div>
                  <div className="tt-modal__student-items">
                    {unreviewedRecords.map(r => (
                      <label key={r.id} className="tt-modal__student-item">
                        <input type="checkbox" checked={unreviewedPicked.has(r.id)} onChange={() => toggleUnreviewed(r.id)} />
                        <span>{r.hoTen}</span>
                        <span className="tt-modal__student-cccd">{r.soCCCD}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="tt-modal__footer">
          <button type="button" className="tt-btn tt-btn--outline" onClick={onClose} disabled={submitting}>
            Hủy
          </button>
          <button
            type="button"
            className="tt-btn tt-btn--danger"
            onClick={handleConfirm}
            disabled={!canConfirm || submitting}
          >
            {submitting ? 'Đang xử lý...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CourseCloseModal;
